import { useState } from 'react';

type Region = 'mena' | 'africa' | 'europe';

interface FaqItem {
  q: string;
  a: string;
}

const regionData: Record<Region, { title: string; subtitle: string; guidelines: { title: string; desc: string; icon: string }[]; faqs: FaqItem[]; currencies: string[] }> = {
  mena: {
    title: 'Middle East & North Africa',
    subtitle: 'Transfer guidelines, banking regulations, and compliance standards for the MENA region.',
    guidelines: [
      {
        title: 'Cross-Border Transfer Limits',
        desc: 'TMT adheres to the electronic funds transfer regulations set by each MENA central bank. Personal transfers are typically capped at USD 25,000 per transaction, with cumulative monthly limits of USD 100,000. Corporate and business accounts may apply for elevated thresholds through our compliance verification process.',
        icon: 'ri-exchange-dollar-line',
      },
      {
        title: 'KYC & Identity Verification',
        desc: 'All MENA-region accounts require government-issued photo identification, proof of address not older than three months, and a live biometric verification scan. TMT\'s AI-powered KYC engine processes verifications within an average of 90 seconds, accepting national IDs, passports, and residence permits issued by any GCC or MENA member state.',
        icon: 'ri-user-search-line',
      },
      {
        title: 'Supported Currencies & Settlement',
        desc: 'TMT supports instant settlement in AED, SAR, QAR, KWD, BHD, OMR, EGP, MAD, JOD, TND, and LBP. Transfers between GCC currencies settle on the same day at mid-market FX rates with a transparent 0.5% margin. Transfers to non-GCC MENA currencies settle within one business day.',
        icon: 'ri-money-dollar-circle-line',
      },
      {
        title: 'Regulatory Bodies & Compliance',
        desc: 'TMT maintains active registrations and ongoing compliance with the Central Bank of the UAE, Saudi Arabian Monetary Authority (SAMA), Qatar Central Bank, Central Bank of Kuwait, Central Bank of Bahrain, and Banque du Liban. Our compliance framework is audited quarterly by an independent Big Four accounting firm.',
        icon: 'ri-building-2-line',
      },
      {
        title: 'Sanctions Screening & AML',
        desc: 'Every MENA-region transaction undergoes real-time screening against UN, EU, OFAC, and local sanctions lists. Our AI-driven anti-money laundering system flags suspicious patterns using behavioral analytics, geolocation correlation, and counterparty network analysis. All flagged transactions are reviewed by a certified AML compliance officer within four hours.',
        icon: 'ri-shield-flash-line',
      },
      {
        title: 'Consumer Protection Rights',
        desc: 'Under MENA consumer protection frameworks, TMT users have the right to dispute unauthorized transactions within 30 days, receive full refunds for failed transfers within 48 hours, and access a dedicated Arabic and English-speaking dispute resolution team. All complaints are acknowledged within two hours and resolved within five business days.',
        icon: 'ri-customer-service-2-line',
      },
    ],
    faqs: [
      { q: 'How long does a transfer to a GCC country take?', a: 'Transfers between GCC countries are processed instantly through our direct banking rails. Funds typically appear in the recipient\'s account within seconds of confirmation.' },
      { q: 'What documents are required for business account verification in MENA?', a: 'Business accounts require a valid trade license, certificate of incorporation, memorandum of association, authorized signatory list, and proof of business address. Verification is completed within two business days.' },
      { q: 'Are there any restrictions on sending money to high-risk jurisdictions?', a: 'TMT complies with FATF grey-list and black-list advisories. Transfers to flagged jurisdictions undergo enhanced due diligence, including source-of-funds verification and beneficial ownership disclosure, and may be subject to extended processing times.' },
      { q: 'How are FX rates determined for MENA currency pairs?', a: 'We source real-time rates from our liquidity providers and apply a transparent margin of 0.5% for GCC currencies and 1.2% for non-GCC currencies. The exact rate is locked in and displayed before you confirm any transfer.' },
      { q: 'Can I receive transfers from government entities in the MENA region?', a: 'Yes. TMT supports incoming transfers from government disbursement systems including salary payments, social welfare distributions, and pension funds. Government transfers are processed through dedicated secure channels with priority settlement.' },
    ],
    currencies: ['AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'EGP', 'MAD', 'JOD', 'TND', 'LBP'],
  },
  africa: {
    title: 'Africa',
    subtitle: 'Regional banking frameworks, mobile money integration, and cross-border payment corridors across the African continent.',
    guidelines: [
      {
        title: 'Mobile Money Integration',
        desc: 'TMT connects directly to major African mobile money platforms including M-Pesa, MTN Mobile Money, Airtel Money, and Orange Money across 35 African markets. Transfers from TMT to any mobile wallet are delivered in under 60 seconds, with confirmation via SMS and in-app notification.',
        icon: 'ri-smartphone-line',
      },
      {
        title: 'Pan-African Payment Corridors',
        desc: 'Through our participation in the Pan-African Payment and Settlement System (PAPSS), TMT facilitates instant cross-border payments in local African currencies without requiring USD intermediation. This reduces transfer costs by up to 60% compared to traditional correspondent banking channels.',
        icon: 'ri-global-line',
      },
      {
        title: 'Agent Network & Cash Pickup',
        desc: 'TMT maintains a network of over 15,000 authorized cash pickup points across 45 African countries through partnerships with leading banks, post offices, and microfinance institutions. Recipients can collect funds with a valid ID and the transaction reference number — no bank account required.',
        icon: 'ri-map-pin-user-line',
      },
      {
        title: 'Regulatory Alignment (AfCFTA)',
        desc: 'TMT\'s African operations align with the African Continental Free Trade Area (AfCFTA) digital payments protocol. We are licensed or partnered with regulated entities in Nigeria (CBN), Kenya (CBK), South Africa (SARB), Ghana (BoG), Ethiopia (NBE), and 12 additional African jurisdictions.',
        icon: 'ri-government-line',
      },
      {
        title: 'Remittance Corridor Transparency',
        desc: 'In compliance with UN SDG 10.c, TMT publishes real-time total cost of remittance data for all African corridors. Our average total cost for sending USD 200 to Africa is 2.1%, significantly below the global average of 6.2% and the SDG target of 3%.',
        icon: 'ri-funds-line',
      },
      {
        title: 'Financial Inclusion Mandate',
        desc: 'TMT\'s African operations are designed to serve the 57% of adults on the continent who remain unbanked. Our tiered KYC system allows basic accounts with simplified documentation (name, phone number, and photo), enabling financial access while maintaining proportionate AML controls.',
        icon: 'ri-hand-heart-line',
      },
    ],
    faqs: [
      { q: 'Which African countries does TMT currently cover?', a: 'TMT supports transfers to 45 African countries. Major markets include Nigeria, Kenya, South Africa, Ghana, Ethiopia, Tanzania, Uganda, Côte d\'Ivoire, Senegal, Cameroon, Rwanda, and Morocco. Our coverage expands monthly.' },
      { q: 'How do mobile money transfers work through TMT?', a: 'Select the recipient\'s country, choose mobile wallet as the delivery method, enter their mobile number and network provider, and confirm. Funds arrive instantly in their mobile wallet. No bank account is needed on either side.' },
      { q: 'What are the fees for sending money to Africa?', a: 'Fees vary by corridor. For tier-1 corridors (Nigeria, Kenya, Ghana), the flat fee is USD 2.99 for amounts up to USD 1,000. FX margins range from 0.8% to 1.5%. The exact total cost is shown transparently before you confirm.' },
      { q: 'Can businesses use TMT for bulk payments to African suppliers?', a: 'Absolutely. Our payroll and bulk disbursement module supports batch payments to bank accounts and mobile wallets across all served African markets, with automated reconciliation reports and tax documentation.' },
      { q: 'Is TMT authorized to operate in my specific African country?', a: 'TMT operates through local partnerships with licensed banks and payment service providers in each jurisdiction. Our regulatory status per country is available in the compliance section of our website. Contact support for specific licensing details.' },
    ],
    currencies: ['NGN', 'KES', 'GHS', 'ZAR', 'ETB', 'TZS', 'UGX', 'XOF', 'XAF', 'RWF', 'MAD', 'EGP'],
  },
  europe: {
    title: 'Europe',
    subtitle: 'SEPA transfers, EU regulatory compliance, and banking standards across European Economic Area member states.',
    guidelines: [
      {
        title: 'SEPA Instant Credit Transfers',
        desc: 'TMT is a SEPA Instant participant, enabling euro-denominated transfers to any SEPA-reachable account in under 10 seconds, 24/7/365. SEPA transfers are processed at zero margin — the interbank rate is passed directly to you with no markup. Standard SEPA transfers settle within one business day at no additional cost.',
        icon: 'ri-flashlight-line',
      },
      {
        title: 'GDPR & Data Protection',
        desc: 'TMT processes all European user data in full compliance with the General Data Protection Regulation (EU 2016/679) and the UK Data Protection Act 2018. Our Frankfurt-based data center ensures EU data residency. Users may request data portability, rectification, or erasure through the privacy dashboard or by contacting our Data Protection Officer.',
        icon: 'ri-lock-star-line',
      },
      {
        title: 'PSD2 & Strong Customer Authentication',
        desc: 'TMT is fully PSD2-compliant across all EEA operations. Every European transaction triggers Strong Customer Authentication (SCA) using two independent elements: biometric verification plus device-based possession. Our open banking APIs are accessible to licensed Third-Party Providers (TPPs) through our PSD2 dedicated interface.',
        icon: 'ri-shield-keyhole-line',
      },
      {
        title: 'EU/EEA Licensing & Passporting',
        desc: 'TMT holds an Electronic Money Institution (EMI) license issued by the Swiss Financial Market Supervisory Authority (FINMA), with full passporting rights across the EEA. Our European operations are supervised by FINMA in coordination with host-state regulators under the EU Payment Services Directive.',
        icon: 'ri-flag-line',
      },
      {
        title: 'Multi-Currency IBAN Accounts',
        desc: 'European users receive a dedicated multi-currency IBAN enabling receipt of EUR, GBP, CHF, SEK, NOK, DKK, PLN, and CZK. Funds can be held, converted, or transferred across currencies at interbank rates. Sub-accounts with unique IBANs are available for business users managing multiple entities or departments.',
        icon: 'ri-bank-line',
      },
      {
        title: 'AML Directive (AMLD6) Compliance',
        desc: 'TMT\'s European AML framework implements all requirements of the Sixth Anti-Money Laundering Directive, including enhanced beneficial ownership verification, centralized AML registers integration, and mandatory reporting of suspicious transactions to the relevant Financial Intelligence Unit within 24 hours.',
        icon: 'ri-file-search-line',
      },
    ],
    faqs: [
      { q: 'How fast are SEPA transfers through TMT?', a: 'SEPA Instant transfers arrive in under 10 seconds. Standard SEPA Credit Transfers settle by the next business day. Both are free of charge — TMT does not mark up SEPA transfers.' },
      { q: 'Can TMT issue a European IBAN for my business?', a: 'Yes. Registered businesses can obtain a dedicated multi-currency IBAN within one business day of verification. Multiple sub-IBANs for different departments or entities are available on our Business Pro plan.' },
      { q: 'How does TMT handle VAT on transaction fees for EU customers?', a: 'TMT applies VAT at the rate applicable in your country of residence for B2C services. VAT-registered businesses within the EU may provide their VAT ID for reverse-charge treatment. VAT invoices are available in the account dashboard.' },
      { q: 'Is my money protected under the EU Deposit Guarantee Scheme?', a: 'As an Electronic Money Institution, client funds are safeguarded in segregated accounts at tier-1 European banks, but are not covered by the Deposit Guarantee Scheme. TMT carries additional private insurance covering client funds up to EUR 100,000 per account.' },
      { q: 'What identity documents are required for European account verification?', a: 'EEA residents need a valid national ID card or passport plus proof of address (utility bill or bank statement, under 3 months old). Non-EEA residents in Europe must also provide their residence permit. Video verification is available for all European users.' },
    ],
    currencies: ['EUR', 'GBP', 'CHF', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'RON', 'HUF', 'BGN'],
  },
};

export default function SupportPage() {
  const [activeRegion, setActiveRegion] = useState<Region>('mena');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const region = regionData[activeRegion];

  return (
    <div className="bg-background-50/95">
      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            {/* Left Text */}
            <div className="w-full lg:w-1/2">
              <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
                / Help Center
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-foreground-50 leading-tight mb-5">
                Support &amp; <span className="text-primary-400">Guidelines</span>
              </h1>
              <p className="text-foreground-300 text-base md:text-lg leading-relaxed font-body">
                Comprehensive money transfer guidelines, banking regulations, and compliance standards for every region we serve.
              </p>
            </div>
            {/* Right Image */}
            <div className="w-full lg:w-1/2">
              <div className="relative overflow-hidden rounded-2xl border border-primary-700/20">
                <div className="absolute inset-0 bg-gradient-to-tr from-background-50/30 via-transparent to-transparent z-10" />
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20customer%20support%20consultant%20at%20modern%20financial%20technology%20office%20warmly%20assisting%20client%20with%20digital%20banking%20inquiry%20soft%20ambient%20lighting%20elegant%20corporate%20environment%20with%20abstract%20financial%20data%20visualization%20screens%20in%20background%20polished%20editorial%20photography%20golden%20warm%20tones%20harmonious%20composition&width=1000&height=1200&seq=support-hero-01&orientation=portrait"
                  alt="TMT Support Team — Professional Customer Assistance"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Region Tabs */}
      <section className="pb-8 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-2 bg-background-100/90 border border-primary-800/20 rounded-full p-1">
            {(['mena', 'africa', 'europe'] as Region[]).map((r) => (
              <button
                key={r}
                onClick={() => { setActiveRegion(r); setExpandedFaq(null); }}
                className={`flex-1 py-2.5 px-4 rounded-full font-label text-xs md:text-sm tracking-wider uppercase transition-all duration-300 ${
                  activeRegion === r
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-400 hover:text-foreground-200'
                }`}
              >
                {r === 'mena' ? 'MENA' : r === 'africa' ? 'Africa' : 'Europe'}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Region Title */}
      <section className="pb-12 md:pb-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-2">{region.title}</h2>
          <p className="text-foreground-400 text-sm md:text-base font-body">{region.subtitle}</p>
        </div>
      </section>

      {/* Guidelines Grid */}
      <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12 bg-background-100/95">
        <div className="max-w-6xl mx-auto">
          <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-6">
            / Regulatory Guidelines
          </span>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {region.guidelines.map((item) => (
              <div
                key={item.title}
                className="p-5 md:p-6 bg-background-50/90 border border-primary-800/20 rounded-xl hover:border-primary-600/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center border border-primary-600/30 rounded-lg">
                    <i className={`${item.icon} text-primary-400 text-lg`} />
                  </div>
                  <div>
                    <h3 className="font-heading text-base md:text-lg text-foreground-100 mb-2">{item.title}</h3>
                    <p className="text-foreground-400 text-sm leading-relaxed font-body">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Currencies */}
      <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-6">
            / Supported Currencies
          </span>
          <div className="flex flex-wrap gap-3">
            {region.currencies.map((currency) => (
              <span
                key={currency}
                className="px-4 py-2 bg-background-100/90 border border-primary-700/20 rounded-full text-foreground-300 text-sm font-label"
              >
                {currency}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12 bg-background-100/95">
        <div className="max-w-4xl mx-auto">
          <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-6">
            / Frequently Asked Questions
          </span>
          <div className="space-y-3">
            {region.faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-primary-800/20 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-background-50/50 transition-colors"
                >
                  <span className="text-foreground-200 text-sm md:text-base font-heading font-medium pr-4">{faq.q}</span>
                  <i className={`ri-${expandedFaq === index ? 'subtract' : 'add'}-line text-primary-400 text-lg flex-shrink-0 transition-transform duration-300`} />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    expandedFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="px-5 pb-5 text-foreground-400 text-sm leading-relaxed font-body">
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Need Help */}
      <section className="py-16 md:py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-xl mx-auto text-center">
          <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center border border-primary-600/30 rounded-full">
            <i className="ri-question-answer-line text-primary-400 text-2xl" />
          </div>
          <h3 className="font-heading text-2xl text-foreground-100 mb-3">Still Have Questions?</h3>
          <p className="text-foreground-400 text-sm leading-relaxed mb-6 font-body">
            Our support team is available 24/7 in English, Arabic, and French. Response time is typically under 5 minutes.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-label text-sm tracking-wider hover:bg-primary-400 transition-colors whitespace-nowrap"
          >
            Contact Support
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </section>
    </div>
  );
}